<template>
  <div v-if="loading" class="grid place-items-center bg-detail-bg" :style="{ height: 'calc(100vh - 85px)' }">
      <Spinner :text="$t('common:text-loading')" />
  </div>
  <VariantUnavailable v-else-if="!variant?.id" class="m-6 lg:m-14 xl:m-16" />
  <div v-else>
    <div class="bg-detail-bg">
      <TopCategoryMenu />
      <ProductDetails :variant="variant" />
    </div>
    <CartCounterButton />
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { sitePages } from "@settings/site-pages.settings";
import { useAuthStore } from '@stores/auth';
import Spinner from "@components/ui/loaders/spinner/spinner";
import ProductDetails from "@components/product/product-details";
import CartCounterButton from "@components/cart/cart-counter-button";
import VariantUnavailable from "@components/product/product-details/variant-unavailable";
import TopCategoryMenu from "@components/category/top-category-menu";

defineI18nRoute({
  paths: {
    cs: '/produkt/[slug]',
    en: '/product/[slug]',
    sk: '/produkt/[slug]',
    pl: '/produkt/[slug]',
    de: '/produkt/[slug]'
  }
});

const config = useRuntimeConfig();
const loading = ref(true);
const route = useRoute();
const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const auth = useAuthStore();
const routeLang = useRouteLang();
const activityChecker = useActivityChecker();

const id = parseInt(route.params.slug);

const variant = ref({});
const setVariant = (data) => {
  _.assignIn(variant.value, data);

  const title = plainText(localized(data, 'title', _.get(data, 'name', '')));
  const description = plainText(localized(data, 'description', _.get(data, 'contents.0.content', '')));
  const canonical = config.public.appBaseUrl + translatePath(ROUTES.CODE.PRODUCT, slugify(data?.id, data?.name));

  useHead({ link: [ { rel: 'canonical', href: canonical } ] });
  useSeoMeta({
    title: title,
    ogTitle: title,
    description: description,
    ogDescription: description,
    ogImage: _.get(data, 'image.url', _.get(data, 'images.0.url', `${config.public.appBaseUrl}${sitePages[config.public.layoutDesign].image}`)),
    ogType: `${sitePages[config.public.layoutDesign].ads[0].metaType}`,
    ogUrl: canonical
  });
}

loading.value = true;
if (_.isInteger(id)) {
  const { data, status } = await useApiFetch(API_ENDPOINTS.ESHOP_PRODUCT_VARIANT_ID, {
    method: 'GET',
    path: { id },
    params: {
      locale: routeLang.locale.value
    }
  });
  if (status.value === 'error') {
    if (import.meta.client && !auth.reloadTimeout) {
      $toast.error(t('error.product-detail.load'));
    }
    sentryCaptureEvent({
      message: 'product/[slug].reload',
      level: 'error',
      extra: { variant: variant.value }
    });
  } else {
    setVariant(data.value);
  }
  loading.value = false;
} else {
  await navigatorTo(translatePath(ROUTES.CODE.ADS));
}

onServerPrefetch(async () => {
  if (!variant.value?.id) {
    throw createError({
      statusCode: 404,
      message: t('text-product-unavailable-title'),
      statusMessage: t('text-product-unavailable-subtitle')
    });
  }
});

const loggedInLoad = async (isLoggedIn) => _.delay(async () => {
  if (isLoggedIn && !variant.value?.isInCustomerFavorites && !variant.value?.isInCustomerLikes && !variant.value?.isInVisitorLikes) {
    _.assignIn(variant.value, _.pick(await productVariantLoad(id) || {}, ['isInCustomerFavorites', 'isInCustomerLikes', 'isInVisitorLikes']));
  }
}, 200);

watch(() => auth.isLoggedIn, loggedInLoad);
watch(() => activityChecker.wasUserActive.value, loggedInLoad);

const variantUpdate = (value) => {
  if (value.id === variant.value.id) {
    variant.value = { ...variant.value, ...value };
  }
};

onMounted(async () => {
  nextTick(async () => await loggedInLoad(auth.isLoggedIn || activityChecker.wasUserActive.value));
  $eventBus.on('variant:update', variantUpdate);
});

onUnmounted(() => {
  $eventBus.off('variant:update', variantUpdate);
});

</script>
